import { useState, useMemo } from "react";
import { PlusIcon } from "../../../assets/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { updateBannerStatus } from "../../../redux/slice/bannerSlice";
import { useToast } from "../../../context/TostContext";
import { routes } from "../../../routes/pageRoutes";
import StatusChip from "../../../components/statusChip/StatusChip";
import ConfirmModal from "../../../components/withModal/ConfirmModal";
import { usePermission } from "../../../hooks/usePermission";
import { TableSearch } from "../../../components/form/Fields";
import CommonButton from "../../../components/commonButton";
import CustomTable from "../../../components/table/CustomTable";

const MODULE = "banner";

const List = ({
  data,
  isLoading,
  isInitialLoad,
  searchTerm,
  setSearchTerm,
  setCurrentPage,
  handleEditBtn,
  pagination,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useToast();
  const { hasPermission } = usePermission();

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusItem, setStatusItem] = useState(null);

  const handleConfirmStatusChange = async () => {
    if (!statusItem) return;
    try {
      const result = await dispatch(
        updateBannerStatus({
          id: statusItem.id,
          is_active: !statusItem.is_active,
        }),
      ).unwrap();
      if (result.res.data.success) {
        toast.success(result.res.data.message || "Status updated");
      }
    } catch (error) {
      toast.error(error.message || "Update failed");
    } finally {
      setShowStatusModal(false);
      setStatusItem(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "sr_no",
        header: "SR NO.",
        cell: (_, row, index) => {
          const page = pagination?.currentPage || 1;
          const size = pagination?.pageSize || 10;
          return (page - 1) * size + index + 1;
        },
      },
      {
        id: "image",
        header: "IMAGE",
        cell: (_, row) =>
          row.image_url ? (
            <img
              src={row.image_url}
              alt="Banner"
              style={{
                width: "80px",
                height: "40px",
                objectFit: "cover",
                borderRadius: "6px",
              }}
            />
          ) : (
            "-"
          ),
      },
      {
        id: "title",
        header: "TITLE",
        cell: (_, row) => row.title || "-",
      },
      {
        id: "context",
        header: "CONTEXT",
        cell: (_, row) =>
          row.context
            ? row.context.charAt(0).toUpperCase() +
            row.context.slice(1).replace("_", " ")
            : "-",
      },
      {
        id: "placement_key",
        header: "PLACEMENT KEY",
        cell: (_, row) =>
          row.placement_key
            ? row.placement_key.charAt(0).toUpperCase() +
            row.placement_key.slice(1)
            : "-",
      },
      {
        id: "action_type",
        header: "ACTION TYPE",
        cell: (_, row) => {
          if (!row.action_type || row.action_type === "none") return "-";
          if (row.action_type === "url") return "URL";
          return "In App";
        }
      },
      {
        id: "action_value",
        header: "ACTION VALUE",
        cell: (_, row) =>
          row.action_value ? (
            row.action_type === "url" ? (
              <a
                href={row.action_value}
                target="_blank"
                rel="noopener noreferrer"
                className="text-decoration-none text-primary"
              >
                {row.action_value}
              </a>
            ) : (
              row.action_value
            )
          ) : (
            "-"
          ),
      },
      {
        id: "status",
        header: "STATUS",
        cell: (_, row) => (
          <StatusChip
            bg={row.is_active ? "success" : "danger"}
            pill
            title={row.is_active ? "Active" : "Inactive"}
            className={hasPermission(MODULE, "update") ? "cursor-pointer" : ""}
            onClick={() => {
              if (hasPermission(MODULE, "update")) {
                setStatusItem(row);
                setShowStatusModal(true);
              }
            }}
          />
        ),
      },
    ],
    [hasPermission, pagination],
  );

  const actionList = useMemo(
    () =>
      [
        hasPermission(MODULE, "read") && {
          title: "View",
          handler: (row) => {
            navigate(routes.addBanner, {
              state: { item: row, isEdit: true, isView: true },
            });
          },
        },
        hasPermission(MODULE, "update") && {
          title: "Edit",
          handler: (row) => {
            handleEditBtn(row);
          },
        },
        hasPermission(MODULE, "update") && {
          title: (row) => (row.is_active ? "Deactivate" : "Activate"),
          handler: (row) => {
            setStatusItem(row);
            setShowStatusModal(true);
          },
        },
      ].filter(Boolean),
    [hasPermission, handleEditBtn, navigate],
  );

  const isTableLoading = !isInitialLoad && isLoading;

  return (
    <>
      <div className="p-4 d-flex align-items-center justify-content-between gap-4">
        <TableSearch
          value={searchTerm}
          placeholder="Search"
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
        />

        {hasPermission(MODULE, "create") && (
          <CommonButton
            icon={<PlusIcon />}
            variant="primary"
            title="Add Banner"
            onClick={(e) => {
              e.stopPropagation();
              navigate(routes.addBanner);
            }}
          />
        )}
      </div>

      <CustomTable
        data={data}
        columns={columns}
        loading={isTableLoading}
        keyField="id"
        emptyMessage="No banners found"
        actionList={actionList}
        pagination={pagination}
      />

      <ConfirmModal
        isOpen={showStatusModal}
        onClose={() => {
          setShowStatusModal(false);
          setStatusItem(null);
        }}
        title={statusItem?.is_active ? "Deactivate Banner" : "Activate Banner"}
        message={
          statusItem?.is_active
            ? "Are you sure you want to deactivate this banner?"
            : "Are you sure you want to activate this banner?"
        }
        onConfirm={handleConfirmStatusChange}
      />
    </>
  );
};

export default List;
